/**
 * ACTIVE ORGANIZATION pointer (multi-org switcher — piece 1 of
 * `infra/guides/MULTI_ORG_SWITCHER.md`).
 *
 * The same user operates N academies (organization) and switches between them with a
 * switcher. The pointer is preference ONLY: it's persisted as a
 * `userId → orgId` map in localStorage (multi-account: each account remembers its
 * active org) and is VALIDATED against the orgs present locally, self-healing to the
 * first one if it was stale/invalid.
 *
 * It's a MODULE-level signal (not component-level): a `setActiveOrg` reactively
 * re-scopes ALL instances of the `use-active-org` hook (header + pages)
 * without reloading.
 *
 * Isolation (piece 5, CRITICAL): when switching org we increment an explicit
 * EPOCH so that deep-linked entity guards discard a pointer that
 * no longer belongs to the active org. See `use-active-org.ts`.
 */

import { createSignal } from "solid-js";

const STORAGE_KEY = "activeOrg"; // userId → orgId map (JSON)

type ActiveOrgMap = Record<string, string>;

function readMap(): ActiveOrgMap {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    // Defensive copy, keeping only the valid string→string entries.
    const out: ActiveOrgMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "string") {
        out[k] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: ActiveOrgMap): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage unavailable — best-effort only.
  }
}

// Module signal: the full userId→orgId map. A change re-scopes everything.
const [orgMap, setOrgMap] = createSignal<ActiveOrgMap>(readMap());

// Explicit-change epoch (piece 5): increments ONLY on an org switch
// by the user, never on boot/self-heal. The isolation guards use it
// to avoid clearing deep-links during initial hydration.
const [orgSwitchEpoch, setOrgSwitchEpoch] = createSignal(0);

/** Reads (reactively) the preferred orgId for a user, without validating. */
function preferredOrgId(userId: string | undefined): string | undefined {
  if (!userId) {
    return undefined;
  }
  return orgMap()[userId];
}

/** Reactive epoch of the last explicit org switch. */
export function activeOrgEpoch(): number {
  return orgSwitchEpoch();
}

/**
 * Sets the user's active org. `epochBump=true` (default) marks an
 * EXPLICIT user switch (triggers isolation guards); the self-heal calls with
 * `false` so as not to trigger the guards during boot.
 */
export function setPreferredOrgId(userId: string, orgId: string, epochBump = true): void {
  const next = { ...orgMap(), [userId]: orgId };
  setOrgMap(next);
  writeMap(next);
  if (epochBump) {
    setOrgSwitchEpoch((e) => e + 1);
  }
}

/**
 * Resolves the ACTIVE org validated against the orgs present. If the pointer is
 * invalid/stale, self-heals to the first available org (without an epoch bump).
 * Returns `undefined` if the user has no orgs.
 */
export function resolveActiveOrgId(
  userId: string | undefined,
  availableOrgIds: string[],
): string | undefined {
  if (!userId || availableOrgIds.length === 0) {
    return undefined;
  }
  const preferred = preferredOrgId(userId);
  if (preferred && availableOrgIds.includes(preferred)) {
    return preferred;
  }
  // Self-heal: stale or missing pointer → first org (without marking a switch).
  const fallback = availableOrgIds[0];
  if (!fallback) {
    return undefined;
  }
  setPreferredOrgId(userId, fallback, false);
  return fallback;
}
